package br.com.htms.inspeccaocampo.platform

import br.com.htms.inspeccaocampo.util.Base64Codec
import kotlin.coroutines.resume
import kotlin.js.Date
import kotlin.js.unsafeCast
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.khronos.webgl.Uint8Array
import org.khronos.webgl.get
import org.w3c.dom.CanvasLineCap
import org.w3c.dom.CanvasLineJoin
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import org.w3c.files.FileReader
import org.w3c.files.get

actual object PlatformServices {
    actual val hasCamera: Boolean get() = true
    actual val hasGps: Boolean get() = true

    actual fun openUrl(url: String) {
        window.open(url, "_blank")
    }

    actual suspend fun captureGps(): GpsFix? {
        val geo = js("navigator.geolocation")
        if (geo == null || geo == undefined) return null
        // Desktop/Wi‑Fi: alta precisão costuma falhar — tenta normal e depois alta precisão.
        return requestPosition(geo, highAccuracy = false, timeoutMs = 20000, maximumAgeMs = 60000)
            ?: requestPosition(geo, highAccuracy = true, timeoutMs = 25000, maximumAgeMs = 0)
    }

    private suspend fun requestPosition(
        geo: dynamic,
        highAccuracy: Boolean,
        timeoutMs: Int,
        maximumAgeMs: Int
    ): GpsFix? = suspendCancellableCoroutine { cont ->
        try {
            val opts = js("{}")
            opts.enableHighAccuracy = highAccuracy
            opts.timeout = timeoutMs
            opts.maximumAge = maximumAgeMs
            geo.getCurrentPosition(
                { pos ->
                    try {
                        val coords = pos.coords
                        val lat = (coords.latitude as Number).toDouble()
                        val lng = (coords.longitude as Number).toDouble()
                        if (cont.isActive) {
                            cont.resume(
                                GpsFix(
                                    latitude = lat,
                                    longitude = lng,
                                    textoLocal = formatGpsCoordenadas(lat, lng)
                                )
                            )
                        }
                    } catch (_: Throwable) {
                        if (cont.isActive) cont.resume(null)
                    }
                },
                { _ ->
                    if (cont.isActive) cont.resume(null)
                },
                opts
            )
        } catch (_: Throwable) {
            if (cont.isActive) cont.resume(null)
        }
    }

    actual suspend fun capturePhoto(): CapturedPhoto? = pickOrCapture(capture = true)

    actual suspend fun pickImage(): CapturedPhoto? = pickOrCapture(capture = false)

    actual suspend fun pickDocument(): PickedDocument? = pickFile("application/pdf,image/jpeg,image/png")

    actual suspend fun pickPlanilha(): PickedDocument? = pickFile(
        ".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
    )

    actual suspend fun pickJson(): PickedDocument? = pickFile(".json,application/json")

    actual suspend fun inflateRaw(data: ByteArray): ByteArray? = suspendCancellableCoroutine { cont ->
        try {
            val stream: dynamic = js("new DecompressionStream('deflate-raw')")
            val blob = org.w3c.files.Blob(arrayOf(data))
            val response = org.w3c.fetch.Response(blob.asDynamic().stream().pipeThrough(stream))
            response.arrayBuffer().then(
                { buf ->
                    val out = org.khronos.webgl.Int8Array(buf).unsafeCast<ByteArray>()
                    if (cont.isActive) cont.resume(out)
                    null
                },
                { if (cont.isActive) cont.resume(null); null }
            )
        } catch (_: Throwable) {
            if (cont.isActive) cont.resume(null)
        }
    }

    private suspend fun pickFile(accept: String): PickedDocument? = suspendCancellableCoroutine { cont ->
        val input = document.createElement("input") as HTMLInputElement
        input.type = "file"
        input.accept = accept
        input.setAttribute(
            "style",
            "position:fixed;left:0;top:0;opacity:0;width:1px;height:1px;z-index:-1;pointer-events:none"
        )
        document.body?.appendChild(input)
        fun finish(result: PickedDocument?) {
            input.remove()
            if (cont.isActive) cont.resume(result)
        }
        input.onchange = { _: Event ->
            val file = input.files?.get(0)
            if (file == null) {
                finish(null)
            } else {
                val reader = FileReader()
                reader.onload = { _: Event ->
                    val buf = reader.result.unsafeCast<org.khronos.webgl.ArrayBuffer>()
                    val u8 = Uint8Array(buf)
                    val out = ByteArray(u8.length)
                    for (i in 0 until u8.length) out[i] = u8[i]
                    finish(PickedDocument(file.name, out))
                }
                reader.onerror = { _: Event -> finish(null) }
                reader.readAsArrayBuffer(file)
            }
        }
        try {
            input.addEventListener("cancel", { finish(null) })
        } catch (_: Throwable) {
        }
        input.click()
    }

    private suspend fun pickOrCapture(capture: Boolean): CapturedPhoto? =
        suspendCancellableCoroutine { cont ->
            // Aborta pick anterior (se houver) sem marcar como "cancel do usuário".
            photoPickContinuation?.takeIf { it.isActive }?.let { prev ->
                photoPickSettled = true
                clearPhotoPickCancelWatch()
                prev.resume(null)
            }
            photoPickSettled = false
            lastPhotoPickCancelled = false
            lastPhotoPickBlockedInApp = false
            clearPhotoPickCancelWatch()
            photoPickContinuation = cont
            cont.invokeOnCancellation {
                clearPhotoPickCancelWatch()
                photoPickContinuation = null
                persistPhotoPickerFlag(false)
            }
            // WhatsApp/IG/FB WebView: câmera/input capture falha ou cancela de falso.
            if (capture && isSocialInAppBrowser()) {
                photoConsoleWarn("Captura bloqueada: navegador in-app (abra no Safari)")
                resumePhotoPick(null, cancelled = false, blockedInApp = true)
                return@suspendCancellableCoroutine
            }
            val input = reusablePhotoInput(capture)
            bindPhotoInput(input)
            try {
                input.value = ""
            } catch (_: Throwable) {
            }
            // iOS: capture=environment provoca reload/cancel falso — só galeria+câmera nativa.
            val useCaptureAttr = capture && !isIosWebClient() && !isSocialInAppBrowser()
            if (useCaptureAttr) {
                input.setAttribute("capture", "environment")
            } else {
                input.removeAttribute("capture")
            }
            // click() primeiro (gesto do usuário / Main.immediate); flag depois.
            input.click()
            persistPhotoPickerFlag(true)
        }

    private fun reusablePhotoInput(capture: Boolean): HTMLInputElement {
        val ios = isIosWebClient()
        val id = if (capture && !ios) "icdPhotoCapture" else "icdPhotoGallery"
        val existing = document.getElementById(id) as? HTMLInputElement
        if (existing != null) return existing
        val input = document.createElement("input") as HTMLInputElement
        input.id = id
        input.type = "file"
        input.accept = "image/jpeg,image/png,image/webp,image/*"
        // Não usar display:none — o iOS Safari ignora click() em input invisível.
        input.setAttribute(
            "style",
            "position:fixed;left:0;top:0;opacity:0;width:1px;height:1px;z-index:-1;pointer-events:none"
        )
        input.tabIndex = -1
        document.body?.appendChild(input)
        return input
    }

    private fun bindPhotoInput(input: HTMLInputElement) {
        val already = try {
            js("input.__icdBound === true") as? Boolean == true
        } catch (_: Throwable) {
            false
        }
        if (already) return
        try {
            js("input.__icdBound = true")
        } catch (_: Throwable) {
        }
        input.onchange = { _: Event ->
            clearPhotoPickCancelWatch()
            val file = input.files?.get(0)
            if (file == null) {
                // change vazio ≈ cancel em alguns browsers — não resume imediato no iOS.
                schedulePhotoPickCancel(input)
            } else {
                readPickedPhotoFile(file)
            }
        }
        try {
            // Safari/iOS: "cancel" costuma disparar ao abrir a câmera, ANTES do change
            // com a foto. Tratar como cancel definitivo só depois de voltar à aba.
            input.addEventListener("cancel", {
                schedulePhotoPickCancel(input)
            })
        } catch (_: Throwable) {
        }
    }

    actual fun encodeSignaturePngBase64(
        strokes: List<List<Pair<Float, Float>>>,
        width: Int,
        height: Int
    ): String? {
        if (width <= 0 || height <= 0 || strokes.isEmpty()) return null
        val box = SignatureInkBounds.of(strokes, width, height) ?: return null
        val outW = box.width.toInt().coerceAtLeast(32)
        val outH = box.height.toInt().coerceAtLeast(24)
        val canvas = document.createElement("canvas") as HTMLCanvasElement
        canvas.width = outW
        canvas.height = outH
        val ctx = canvas.getContext("2d") as? CanvasRenderingContext2D ?: return null
        // Fundo transparente — no PDF usamos Multiply para sumir branco residual.
        ctx.clearRect(0.0, 0.0, outW.toDouble(), outH.toDouble())
        ctx.strokeStyle = "#000000"
        ctx.lineWidth = 5.0
        ctx.lineCap = "round".unsafeCast<CanvasLineCap>()
        ctx.lineJoin = "round".unsafeCast<CanvasLineJoin>()
        val ox = box.minX.toDouble()
        val oy = box.minY.toDouble()
        strokes.forEach { stroke ->
            if (stroke.size < 2) return@forEach
            ctx.beginPath()
            ctx.moveTo(stroke.first().first.toDouble() - ox, stroke.first().second.toDouble() - oy)
            stroke.drop(1).forEach { p ->
                ctx.lineTo(p.first.toDouble() - ox, p.second.toDouble() - oy)
            }
            ctx.stroke()
        }
        val dataUrl = canvas.toDataURL("image/png")
        val b64 = dataUrl.substringAfter("base64,", "")
        return b64.ifBlank { null }
    }

    actual fun decodeBase64(base64: String): ByteArray? = Base64Codec.decode(base64)

    actual fun encodeBase64(bytes: ByteArray): String = Base64Codec.encode(bytes)

    actual suspend fun loadImageBytes(path: String): ByteArray? {
        // 1) memória / alias / localStorage
        PlatformFiles.readBytes(path)?.takeIf { it.isNotEmpty() }?.let {
            consoleWarn("loadImageBytes: hit memória ($path, ${it.size}b)")
            return it
        }
        // 2) IndexedDB on-demand (warmUp pode ter falhado ou valor era Blob)
        PlatformFiles.readBytesAsync(path)?.takeIf { it.isNotEmpty() }?.let {
            consoleWarn("loadImageBytes: hit IDB async ($path, ${it.size}b)")
            return it
        }
        // 3) R2 / URL remota (path do form OU alias remoto)
        val remoteCandidates = buildList {
            if (R2Photos.isRemotePath(path)) add(path)
            PlatformFiles.aliasOf(path)?.takeIf { R2Photos.isRemotePath(it) }?.let { add(it) }
        }
        for (remotePath in remoteCandidates) {
            val remote = R2Photos.fetchBytes(remotePath)
            if (remote != null && remote.isNotEmpty()) {
                PlatformFiles.writeBytesDurable(path, remote)
                if (remotePath != path) {
                    PlatformFiles.writeBytesDurable(remotePath, remote)
                    PlatformFiles.linkAlias(path, remotePath)
                }
                consoleWarn("loadImageBytes: hit R2 ($remotePath, ${remote.size}b)")
                return remote
            }
            consoleWarn("loadImageBytes: falha R2/remoto para $remotePath")
        }
        consoleWarn("loadImageBytes: SEM BYTES para $path")
        return null
    }

    actual suspend fun loadBundledAsset(fileName: String): ByteArray? {
        val name = fileName.trim().trimStart('/')
        if (name.isBlank() || name.contains("..")) return null
        return suspendCancellableCoroutine { cont ->
            try {
                val promise: dynamic = js("fetch")(name)
                promise.then(
                    { response ->
                        val ok = (response.ok as? Boolean) == true
                        if (!ok) {
                            if (cont.isActive) cont.resume(null)
                        } else {
                            response.arrayBuffer().then(
                                { buf ->
                                    val u = org.khronos.webgl.Uint8Array(buf as org.khronos.webgl.ArrayBuffer)
                                    val out = ByteArray(u.length) { i -> u[i] }
                                    if (cont.isActive) cont.resume(out)
                                },
                                { if (cont.isActive) cont.resume(null) }
                            )
                        }
                        null
                    },
                    { if (cont.isActive) cont.resume(null); null }
                )
            } catch (_: Throwable) {
                if (cont.isActive) cont.resume(null)
            }
        }
    }

    /**
     * Persiste a foto carimbada (memória + IndexedDB await) e sobe ao R2 quando possível.
     * Se R2 ok: devolve a URL remota (como antes do download-fix) para o PDF poder
     * buscar via rede mesmo se o cache local sumir. Mantém alias local ↔ URL.
     * Se R2 falhar: mantém o path local.
     */
    actual suspend fun publishPhoto(localPath: String): String {
        val bytes = PlatformFiles.readBytes(localPath)
            ?: PlatformFiles.readBytesAsync(localPath)
        if (bytes == null || bytes.isEmpty()) {
            consoleWarn("publishPhoto: sem bytes em $localPath")
            return localPath
        }
        val durableOk = PlatformFiles.writeBytesDurable(localPath, bytes)
        if (!durableOk) {
            consoleWarn("publishPhoto: IndexedDB falhou; mantendo só memória ($localPath)")
        } else {
            consoleWarn("publishPhoto: IDB ok ($localPath, ${bytes.size}b)")
        }
        // Offline: não tenta R2 (evita fetch longo / pressão de memória no mobile).
        if (!isNetworkAvailable()) {
            consoleWarn("publishPhoto: offline — mantendo path local $localPath")
            return localPath
        }
        val url = runCatching { R2Photos.upload(bytes) }.getOrNull()
        if (url.isNullOrBlank()) {
            consoleWarn("publishPhoto: R2 indisponível; path local $localPath")
            return localPath
        }
        PlatformFiles.writeBytesDurable(url, bytes)
        PlatformFiles.linkAlias(localPath, url)
        consoleWarn("publishPhoto: R2 ok, path remoto no form ($localPath ↔ $url)")
        return url
    }

    private fun consoleWarn(msg: String) {
        try {
            js("console").warn("[ICD Services] $msg")
        } catch (_: Throwable) {
        }
    }

    private fun isLikelyHeic(bytes: ByteArray): Boolean =
        bytes.size >= 12 &&
            bytes[4] == 'f'.code.toByte() &&
            bytes[5] == 't'.code.toByte() &&
            bytes[6] == 'y'.code.toByte() &&
            bytes[7] == 'p'.code.toByte() &&
            (bytes[8] == 'h'.code.toByte() || bytes[8] == 'm'.code.toByte())

    actual suspend fun saveBannerImage(bytes: ByteArray): String? {
        val path = PlatformFiles.join(PlatformFiles.documentsDir(), "app_config/banner_custom.jpg")
        PlatformFiles.ensureDir(PlatformFiles.join(PlatformFiles.documentsDir(), "app_config"))
        val jpeg = PlatformImage.toJpegBytes(bytes) ?: bytes
        PlatformFiles.writeBytes(path, jpeg)
        return path
    }

    actual fun shouldEmbedPhotoBytesInForm(): Boolean = true

    actual fun isNetworkAvailable(): Boolean {
        // iOS Safari marca onLine=false no handoff 4G e isso pulava retries.
        if (isIosWebClient()) return true
        return try {
            (js("navigator.onLine") as? Boolean) != false
        } catch (_: Throwable) {
            true
        }
    }

    actual fun setUnloadGuard(active: Boolean) {
        unloadGuardActive = active
        ensureUnloadGuardInstalled()
        persistUploadInflightFlag(active)
    }

    actual fun consumeUploadInflightFlag(): Boolean {
        return try {
            val pending = localStorage.getItem(UPLOAD_INFLIGHT_KEY) == "1"
            if (pending) localStorage.removeItem(UPLOAD_INFLIGHT_KEY)
            pending
        } catch (_: Throwable) {
            false
        }
    }

    actual fun setBackgroundFlush(onFlush: (() -> Unit)?) {
        backgroundFlush = onFlush
        ensureUnloadGuardInstalled()
    }

    actual fun setForegroundResume(onResume: (() -> Unit)?) {
        foregroundResume = onResume
        ensureUnloadGuardInstalled()
    }

    actual fun notifyPhotoPickerOpening() {
        persistPhotoPickerFlag(true)
        // Não flush aqui: o rascunho já foi gravado em obterFotoCapturada antes do click.
        // Flush síncrono antes do click() atrasa o gesto e no iOS pode gerar cancel falso.
    }

    actual fun consumePhotoPickerInterrupted(): Boolean {
        return try {
            val pending = localStorage.getItem(PHOTO_PICKER_KEY) == "1"
            if (pending) localStorage.removeItem(PHOTO_PICKER_KEY)
            pending
        } catch (_: Throwable) {
            false
        }
    }

    actual fun consumeLastPhotoPickCancelled(): Boolean {
        val v = lastPhotoPickCancelled
        lastPhotoPickCancelled = false
        return v
    }

    actual fun consumeLastPhotoBlockedInAppBrowser(): Boolean {
        val v = lastPhotoPickBlockedInApp
        lastPhotoPickBlockedInApp = false
        return v
    }

    actual fun dismissSoftKeyboard() {
        try {
            js("var el=document.activeElement;if(el&&el.blur)el.blur()")
        } catch (_: Throwable) {
        }
    }
}

private var unloadGuardActive = false
private var unloadGuardInstalled = false
private var backgroundFlush: (() -> Unit)? = null
private var foregroundResume: (() -> Unit)? = null
private var photoPickContinuation: kotlinx.coroutines.CancellableContinuation<CapturedPhoto?>? = null
private var photoPickSettled = false
private var lastPhotoPickCancelled = false
private var lastPhotoPickBlockedInApp = false
private var photoPickCancelTimerId: Int? = null
private var photoPickVisibilityHandler: ((Event) -> Unit)? = null
private const val UPLOAD_INFLIGHT_KEY = "icd_upload_inflight"
private const val PHOTO_PICKER_KEY = "icd_photo_picker"

private fun photoConsoleWarn(msg: String) {
    try {
        js("console.warn")(msg)
    } catch (_: Throwable) {
    }
}

private fun bytesLookLikeHeic(bytes: ByteArray): Boolean =
    bytes.size >= 12 &&
        bytes[4] == 'f'.code.toByte() &&
        bytes[5] == 't'.code.toByte() &&
        bytes[6] == 'y'.code.toByte() &&
        bytes[7] == 'p'.code.toByte() &&
        (bytes[8] == 'h'.code.toByte() || bytes[8] == 'm'.code.toByte())

private fun readPickedPhotoFile(file: org.w3c.files.File) {
    clearPhotoPickCancelWatch()
    lastPhotoPickCancelled = false
    val reader = FileReader()
    reader.onload = {
        val result = reader.result
        val buffer = result as? org.khronos.webgl.ArrayBuffer
        if (buffer == null) {
            resumePhotoPick(null, cancelled = false)
        } else {
            val uint8 = Uint8Array(buffer)
            val bytes = ByteArray(uint8.length) { i -> uint8[i] }
            val path = PlatformFiles.join(
                PlatformFiles.documentsDir(),
                "fotos/web_${Date().getTime().toLong()}.jpg"
            )
            PlatformFiles.ensureDir(PlatformFiles.join(PlatformFiles.documentsDir(), "fotos"))
            GlobalScope.launch {
                try {
                    val jpeg = PlatformImage.toJpegBytes(bytes)
                    if (jpeg == null || jpeg.isEmpty()) {
                        val heic = bytesLookLikeHeic(bytes)
                        photoConsoleWarn(
                            if (heic) "Captura HEIC sem conversão — use a câmera (JPEG) ou foto já em JPG/PNG"
                            else "Captura: não foi possível converter para JPEG"
                        )
                        resumePhotoPick(null, cancelled = false)
                    } else {
                        val ok = PlatformFiles.writeBytesDurable(path, jpeg)
                        if (!ok) {
                            photoConsoleWarn("IDB falhou na captura; usando memória")
                        }
                        resumePhotoPick(CapturedPhoto(path = path), cancelled = false)
                    }
                } catch (t: Throwable) {
                    photoConsoleWarn("Captura falhou: ${t.message}")
                    resumePhotoPick(null, cancelled = false)
                }
            }
        }
        Unit
    }
    reader.onerror = {
        resumePhotoPick(null, cancelled = false)
    }
    reader.readAsArrayBuffer(file)
}

/** iPhone/iPad/iPod, inclusive iPadOS com UA de desktop. */
internal fun isIosWebClient(): Boolean {
    return try {
        val ua = (js("navigator.userAgent") as? String) ?: ""
        val platform = (js("navigator.platform") as? String) ?: ""
        val maxTouch = (js("navigator.maxTouchPoints") as? Number)?.toInt() ?: 0
        ua.contains("iPhone", ignoreCase = true) ||
            ua.contains("iPad", ignoreCase = true) ||
            ua.contains("iPod", ignoreCase = true) ||
            (platform.contains("MacIntel", ignoreCase = true) && maxTouch > 1)
    } catch (_: Throwable) {
        false
    }
}

/**
 * WhatsApp / Instagram / Facebook e similares (WKWebView in-app).
 * Câmera e WASM costumam falhar — preferir Safari.
 */
internal fun isSocialInAppBrowser(): Boolean {
    return try {
        val flagged = js("window.__ICD_IS_INAPP_BROWSER__ === true") as? Boolean
        if (flagged == true) return true
        val ua = (js("navigator.userAgent") as? String) ?: ""
        ua.contains("WhatsApp", ignoreCase = true) ||
            ua.contains("FBAN", ignoreCase = true) ||
            ua.contains("FBAV", ignoreCase = true) ||
            ua.contains("FBIOS", ignoreCase = true) ||
            ua.contains("Instagram", ignoreCase = true) ||
            ua.contains("Line/", ignoreCase = true) ||
            ua.contains("Twitter", ignoreCase = true) ||
            ua.contains("TikTok", ignoreCase = true) ||
            ua.contains("BytedanceWebview", ignoreCase = true) ||
            ua.contains("Snapchat", ignoreCase = true) ||
            ua.contains("MicroMessenger", ignoreCase = true) ||
            ua.contains("Messenger", ignoreCase = true) ||
            ua.contains("LinkedInApp", ignoreCase = true) ||
            ua.contains("Pinterest", ignoreCase = true)
    } catch (_: Throwable) {
        false
    }
}

/** Safari (iOS ou macOS) — fetch+keepalive estoura no multipart grande. */
internal fun isSafariOrIosWebClient(): Boolean {
    if (isIosWebClient()) return true
    return try {
        val ua = (js("navigator.userAgent") as? String) ?: ""
        ua.contains("Safari", ignoreCase = true) &&
            !ua.contains("Chrome", ignoreCase = true) &&
            !ua.contains("Chromium", ignoreCase = true) &&
            !ua.contains("CriOS", ignoreCase = true) &&
            !ua.contains("FxiOS", ignoreCase = true) &&
            !ua.contains("EdgiOS", ignoreCase = true) &&
            !ua.contains("Edg/", ignoreCase = true) &&
            !ua.contains("Android", ignoreCase = true)
    } catch (_: Throwable) {
        false
    }
}

private fun persistUploadInflightFlag(active: Boolean) {
    try {
        if (active) {
            localStorage.setItem(UPLOAD_INFLIGHT_KEY, "1")
        } else {
            localStorage.removeItem(UPLOAD_INFLIGHT_KEY)
        }
    } catch (_: Throwable) {
    }
}

private fun persistPhotoPickerFlag(active: Boolean) {
    try {
        if (active) {
            localStorage.setItem(PHOTO_PICKER_KEY, "1")
        } else {
            localStorage.removeItem(PHOTO_PICKER_KEY)
        }
    } catch (_: Throwable) {
    }
}

private fun clearPhotoPickCancelWatch() {
    photoPickCancelTimerId?.let { id ->
        try {
            window.clearTimeout(id)
        } catch (_: Throwable) {
        }
    }
    photoPickCancelTimerId = null
    photoPickVisibilityHandler?.let { handler ->
        try {
            document.removeEventListener("visibilitychange", handler)
        } catch (_: Throwable) {
        }
    }
    photoPickVisibilityHandler = null
}

/**
 * iOS Safari dispara "cancel" ao abrir a câmera, antes do usuário tirar a foto.
 * Só confirma cancelamento depois que a página volta a ficar visível e o input
 * continua sem arquivo (dando tempo do evento "change" vencer a corrida).
 */
private fun schedulePhotoPickCancel(input: HTMLInputElement) {
    if (photoPickSettled) return
    clearPhotoPickCancelWatch()

    fun isDocHidden(): Boolean = try {
        js("document.visibilityState") as? String == "hidden"
    } catch (_: Throwable) {
        false
    }

    fun waitUntilVisibleThenSettle() {
        clearPhotoPickCancelWatch()
        val handler: (Event) -> Unit = handler@{ _ ->
            if (isDocHidden()) return@handler
            clearPhotoPickCancelWatch()
            photoPickCancelTimerId = window.setTimeout({
                photoPickCancelTimerId = null
                if (photoPickSettled) return@setTimeout
                if (isDocHidden()) {
                    waitUntilVisibleThenSettle()
                    return@setTimeout
                }
                val file = try {
                    input.files?.get(0)
                } catch (_: Throwable) {
                    null
                }
                if (file != null) {
                    readPickedPhotoFile(file)
                } else {
                    // In-app: cancel genérico vira orientação Safari.
                    val blocked = isSocialInAppBrowser()
                    resumePhotoPick(null, cancelled = !blocked, blockedInApp = blocked)
                }
            }, 550)
        }
        photoPickVisibilityHandler = handler
        try {
            document.addEventListener("visibilitychange", handler)
        } catch (_: Throwable) {
        }
        photoPickCancelTimerId = window.setTimeout({
            photoPickCancelTimerId = null
            clearPhotoPickCancelWatch()
            if (photoPickSettled) return@setTimeout
            val file = try {
                input.files?.get(0)
            } catch (_: Throwable) {
                null
            }
            if (file != null) {
                readPickedPhotoFile(file)
            } else {
                val blocked = isSocialInAppBrowser()
                resumePhotoPick(null, cancelled = !blocked, blockedInApp = blocked)
            }
        }, 180_000)
    }

    fun settleIfStillEmpty() {
        if (photoPickSettled) return
        if (isDocHidden()) {
            waitUntilVisibleThenSettle()
            return
        }
        val file = try {
            input.files?.get(0)
        } catch (_: Throwable) {
            null
        }
        if (file != null) {
            readPickedPhotoFile(file)
        } else {
            val blocked = isSocialInAppBrowser()
            resumePhotoPick(null, cancelled = !blocked, blockedInApp = blocked)
        }
    }

    if (isDocHidden()) {
        waitUntilVisibleThenSettle()
        return
    }

    // Página ainda visível: no iOS o cancel costuma vir um instante antes de ir
    // para a câmera. Espera; se esconder, passa a aguardar o retorno.
    // In-app WebViews (WhatsApp) precisam de margem maior.
    val delayMs = when {
        isSocialInAppBrowser() -> 1200
        isIosWebClient() -> 900
        else -> 280
    }
    photoPickCancelTimerId = window.setTimeout({
        photoPickCancelTimerId = null
        if (photoPickSettled) return@setTimeout
        if (isDocHidden()) {
            waitUntilVisibleThenSettle()
        } else {
            settleIfStillEmpty()
        }
    }, delayMs)
}

private fun resumePhotoPick(
    photo: CapturedPhoto?,
    cancelled: Boolean = false,
    blockedInApp: Boolean = false
) {
    if (photoPickSettled) return
    photoPickSettled = true
    clearPhotoPickCancelWatch()
    lastPhotoPickCancelled = cancelled && photo == null && !blockedInApp
    lastPhotoPickBlockedInApp = blockedInApp && photo == null
    if (photo != null || cancelled || blockedInApp) {
        persistPhotoPickerFlag(false)
    } else {
        // Falha de processamento: limpa flag para não fingir "reload da câmera".
        persistPhotoPickerFlag(false)
    }
    val cont = photoPickContinuation
    photoPickContinuation = null
    if (cont != null && cont.isActive) {
        cont.resume(photo)
    }
}

private fun ensureUnloadGuardInstalled() {
    if (unloadGuardInstalled) return
    unloadGuardInstalled = true
    try {
        window.addEventListener("beforeunload", { e ->
            if (unloadGuardActive) {
                e.preventDefault()
                // Chromium: returnValue vazio mostra o aviso nativo.
                // Não usar asDynamic() — quebra no runtime Web (TypeError).
                val setRv: dynamic = js("(function(ev){ev.returnValue='';})")
                setRv(e)
            }
        })
    } catch (_: Throwable) {
    }
    // iOS ignora beforeunload; pagehide/visibilitychange não devem abortar o POST.
    try {
        window.addEventListener("pagehide", {
            if (unloadGuardActive) persistUploadInflightFlag(true)
            runCatching { backgroundFlush?.invoke() }
        })
    } catch (_: Throwable) {
    }
    try {
        document.addEventListener("visibilitychange", {
            val hidden = try {
                js("document.visibilityState") as? String == "hidden"
            } catch (_: Throwable) {
                false
            }
            if (hidden) {
                if (unloadGuardActive) persistUploadInflightFlag(true)
                runCatching { backgroundFlush?.invoke() }
            } else {
                runCatching { foregroundResume?.invoke() }
            }
        })
    } catch (_: Throwable) {
    }
}
